import gql from 'graphql-tag';

const FRAGMENT = gql`
  fragment Conversation on conversations {
    id: convo_id
    convo_id
    created_by
    updated_at
    type
    subject
    unactioned
    users {
      user {
        ...UserSummary
      }
    }
    student {
      ...UserSummary
    }
  }
  fragment Message on messages {
    id
    convo_id
    incoming
    type
    text
    html
    subject
    attachments
    created_by
    created_at
    user {
      ...UserSummary
    }
    receipts {
      ...Receipt
    }
  }
  fragment ThreadItem on conversations {
    ...Conversation
    messages(limit: 1, order_by: [{ created_at: desc }]) {
      ...Message
    }
  }
  fragment UserSummary on users {
    user_id
    user_cognito_sub
    name
    user_first_name
    user_last_name
    user_email
    user_phone
    user_type
    avatar: user_pic
  }
  fragment Receipt on message_receipts {
    id
    status
    user {
      ...UserSummary
    }
  }
`;

export const GET_CONVOS = gql`
  query getConvos($type: String!) {
    conversations(
      where: { type: { _eq: $type }, archived: { _neq: true } }
      order_by: [{ unactioned: desc }, { updated_at: desc }]
    ) {
      ...ThreadItem
    }
  }
  ${FRAGMENT}
`;

export const CONVO_SEARCH = gql`
  subscription listenConvos(
    $type: String!
    $match: String
    $offset: Int
    $userId: Int!
    $archived: Boolean_comparison_exp
  ) {
    conversations: v_conversations(
      where: {
        user_id: { _eq: $userId }
        type: { _eq: $type }
        student_id: { _is_null: false }
        _or: [
          { users: { user: { user_first_name: { _ilike: $match } } } }
          { users: { user: { user_last_name: { _ilike: $match } } } }
        ]
        archived: $archived
      }
      limit: 20
      offset: $offset
    ) {
      id: convo_id
      convo_id
      type
      unread
      subject
      message
      users {
        user {
          ...UserSummary
        }
      }
      student {
        ...UserSummary
      }
      created_at
      updated_at
      last_message_at
    }
  }
  ${FRAGMENT}
`;

export const GET_MY_SMS_CONVOS = gql`
  subscription getMySMSConvos($userId: Int!, $query: String, $unread: Boolean) {
    rows: v_conversations(
      where: { 
        type: { _eq: "sms" }, 
        user_id: { _eq: $userId }
        unread: {_eq: $unread }  
        convo_user:{ 
          deleted_at: {_is_null:true},
          _or: [
            { name: {_ilike: $query }},
            { user_last_name: {_ilike: $query }},
          ]
        }
      }
      order_by: [{ unread: desc }, { last_message_at: desc}]
    ) {
      id: convo_id
      user: convo_user {
        user_id
        name
        user_last_name
        user_type
        user_pic
      }
      message
      unread
      last_message_at
    }
  }
`;

export const GET_ALL_SMS_CONVO = gql`
    query getAllSMSConos($query: String) {
      rows: conversations(where: {
        type: { _eq: "sms" },
        user: {
          _or: [
            { name: {_ilike: $query }},
            { user_last_name: {_ilike: $query }},
          ]
        }
      }) {
        id: convo_id
        user {
          user_id
          name
          user_last_name
          user_type
          user_pic
        }
      }
    }
`;

export const ALL_EMAIL_CONVOS = gql`
  subscription allConvos(
    $type: String!
    $match: String
    $offset: Int
    $archived: Boolean_comparison_exp
  ) {
    conversations: v_conversations(
      where: {
        type: { _eq: $type }
        student_id: { _is_null: false }
        _or: [
          { users: { user: { user_first_name: { _ilike: $match } } } }
          { users: { user: { user_last_name: { _ilike: $match } } } }
        ]
        archived: $archived
      }
      limit: 20
      offset: $offset
      distinct_on: [last_message_at]
      order_by: [{ last_message_at: desc_nulls_last }]
    ) {
      id: convo_id
      convo_id
      type
      subject
      message
      last_message_at
      created_at
      updated_at
      users {
        user {
          ...UserSummary
        }
      }
      student {
        ...UserSummary
      }
    }
  }
  ${FRAGMENT}
`;

export const CREATE_CONVO = gql`
  mutation addConvo($data: [conversations_insert_input!]!) {
    rows: insert_conversations(objects: $data) {
      returning {
        ...ThreadItem
      }
    }
  }
  ${FRAGMENT}
`;

export const ADD_MESSAGE = gql`
  mutation addMessage($data: messages_insert_input!) {
    row: insert_messages_one(object: $data) {
      ...Message
      recipients {
        id
        user {
          ...UserSummary
        }
      }
    }
  }
  ${FRAGMENT}
`;

export const GET_CONVO_DETAILS = gql`
  subscription getConvoDetails($id: uuid!) {
    convo: conversations_by_pk(convo_id: $id) {
      id: convo_id
      type
      convo_id
      subject
      group {
        name
      }
      student {
        ...UserSummary
      }
      user {
        ...UserSummary
        user_phone
        guardian_children {
          student {
            ...UserSummary
          }
        }
        student_guardians {
          guardian {
            ...UserSummary
          }
        }
      }
      users {
        user {
          user_type
          ...UserSummary
        }
      }
      messages(order_by: [{ created_at: asc }]) {
        ...Message
        recipients {
          message {
            receipts {
              status
            }
          }
          id
          user {
            ...UserSummary
          }
        }
      }
    }
  }
  ${FRAGMENT}
`;

export const GET_STUDENTS_PROFILES = gql`
  query getProfiles($userId: Int) {
    rows: student_profile(
      where: { user_id: { _eq: $userId } }
      order_by: [{ user: { user_first_name: asc, user_last_name: asc } }]
    ) {
      id
      user_id
      user {
        ...UserSummary
      }
      hc_user {
        ...UserSummary
      }
      lc_user {
        ...UserSummary
      }
      guardians {
        guardian {
          ...UserSummary
        }
      }
    }
  }
  ${FRAGMENT}
`;

export const GET_STUDENTS = gql`
  query getStudents {
    rows: v_students(
      order_by: [{ user_first_name: asc, user_last_name: asc }]
    ) {
      id: user_id
      user_id
      user_first_name
      user_last_name
      user_email
      user_phone
      hc_user: hc {
        ...UserSummary
      }
      lc_user: lc {
        ...UserSummary
      }
      guardians {
        guardian {
          ...UserSummary
        }
      }
    }
  }
  ${FRAGMENT}
`;

export const ADD_USER_TO_CONVO = gql`
  mutation addUser($convoId: uuid!, $userId: Int!) {
    insert_conversation_users_one(
      object: { convo_id: $convoId, user_id: $userId }
    ) {
      id: convo_id
      convo_id
      conversation {
        users {
          user {
            ...UserSummary
          }
        }
      }
    }
  }
  ${FRAGMENT}
`;

export const UPDATE_CONVO = gql`
  mutation updateConvo($id: uuid!, $input: conversations_set_input!) {
    update_conversations_by_pk(pk_columns: { convo_id: $id }, _set: $input) {
      id: convo_id
      convo_id
      unactioned
    }
  }
`;

export const GET_UNREAD_COUNT = gql`
  subscription getUnreadCount($type: String, $userId: Int, $isGroup: Boolean) {
    result: v_convo_unread_counts_aggregate(
      where: {
        type: { _eq: $type }
        user_id: { _eq: $userId }
        is_group: { _eq: $isGroup }
      }
    ) {
      aggregate {
        sum {
          count
        }
      }
    }
  }
`;

export const GET_STUDENTS_FOR_SMS = gql`
  subscription getStudents($query: String, $userId: Int!) {
    students: v_students(
      where: {
        _or: [
          { user_first_name: { _ilike: $query } }
          { user_last_name: { _ilike: $query } }
        ]
      }
    ) {
      id: user_id
      user_first_name
      user_last_name
      user_pic
      assigned_conversations(
        where: {
          type: { _eq: "sms" }
          unread_count: { user_id: { _eq: $userId } }
        }
        order_by: [{ last_message_at: desc_nulls_last }]
      ) {
        last_message_at
        unread
      }
      conversations(where: { type: { _eq: "sms" } }) {
        messages(order_by: { created_at: desc }, limit: 1) {
          created_at
        }
      }
    }
  }
`;

export const GET_STUDENT_CONVOS_SMS = gql`
  subscription get($userId: Int!) {
    student: users_by_pk(user_id: $userId) {
      id: user_id
      sub: user_cognito_sub
      user_first_name
      user_last_name
      guardians: student_guardians(
        where: { guardian: { user_cognito_sub: { _is_null: false } } }
      ) {
        guardian {
          id: user_id
          sub: user_cognito_sub
          user_first_name
          user_last_name
        }
      }
    }
  }
`;

export const GET_USER_SMS_CONVO = gql`
  query getUserSMSConvo($sub: uuid!) {
    users(where: { user_cognito_sub: { _eq: $sub } }) {
      user_phone
      user_type
      user_id
    }
    convo: conversations_by_pk(convo_id: $sub) {
      convo_id
      student_id
    }
  }
`;

export const GET_GROUP_SMS_CONVO = gql`
  query getUserSMSConvo($groupId: uuid!, $convoId: uuid!) {
    user: groups_by_pk(id: $groupId) {
      group_users {
        user_id
        user {
          user_phone
        }
      }
    }
    conversations(
      where: { type: { _eq: "sms" }, convo_id: { _eq: $convoId } }
      order_by: [{ created_at: desc }]
    ) {
      convo_id
    }
  }
`;

export const GET_CONVO_BY_ID = gql`
  query getConvoById($id: uuid!) {
    convo: conversations_by_pk(convo_id: $id) {
      id: convo_id
    }
  }
`;

export const GET_GROUPS_FOR_SMS = gql`
  subscription getGroups($query: String, $userId: Int!) {
    groups(where: { name: { _ilike: $query } }) {
      id
      name
      sms_unread_counts_aggregate(where: { user_id: { _eq: $userId } }) {
        aggregate {
          count
        }
      }
    }
  }
`;

export const GET_GROUP_CONVOS_SMS = gql`
  subscription get($groupId: uuid!) {
    group: groups_by_pk(id: $groupId) {
      id
      name
      conversation {
        id: convo_id
        unactioned
        type
        users {
          user_id
        }
      }
    }
  }
`;

export const UPDATE_USER_CONVO_STATUS = gql`
  mutation updateUserConvo(
    $convoId: uuid!
    $userId: Int!
    $timestamp: timestamptz!
  ) {
    update_conversation_users(
      where: { convo_id: { _eq: $convoId }, user_id: { _eq: $userId } }
      _set: { last_activity: $timestamp }
    ) {
      returning {
        convo_id
        user_id
        last_activity
      }
    }
  }
`;

export const JOIN_CONVO = gql`
  mutation joinUserConvo(
    $convoId: uuid!
    $userId: Int!
    $timestamp: timestamptz!
  ) {
    insert_conversation_users_one(
      object: {
        user_id: $userId
        convo_id: $convoId
        last_activity: $timestamp
      }
      on_conflict: {
        constraint: conversation_users_convo_id_user_id_key
        update_columns: [last_activity]
      }
    ) {
      convo_id
      user_id
      last_activity
    }
  }
`;

export const GET_CONVO_COACHES = gql`
  query getConvoCoaches($id: uuid!) {
    convo: conversations_by_pk(convo_id: $id) {
      group {
        lc {
          user_id
          user_first_name
          user_last_name
          avatar: user_pic
        }
        hc {
          user_id
          user_first_name
          user_last_name
          avatar: user_pic
        }
      }
      student {
        student_profiles {
          lc_user {
            user_id
            user_first_name
            user_last_name
            avatar: user_pic
          }
          hc_user {
            user_id
            user_first_name
            user_last_name
            avatar: user_pic
          }
        }
      }
    }
  }
`;
